'use client';
import React, { useState } from 'react';
import Button from './ui/Button';

// Notes:
// - Uses top-level navigation via window.open to let the browser handle the download / SafeBrowsing prompts.
// - After the user returns to the page (visibilitychange), we restore scroll/focus to the Get Started card to avoid the "zoomed"/broken view on mobile.


// Client-side download button that requests a short-lived signed URL from the server.
// For better protection, configure reCAPTCHA v3 and set NEXT_PUBLIC_RECAPTCHA_SITE_KEY in your env.
// Also ensure the site includes the reCAPTCHA script in `src/app/layout.jsx`:
// <script src="https://www.google.com/recaptcha/api.js?render=YOUR_SITE_KEY"></script>

export default function DownloadApkButton({ app = 'child', className = '', label, variant = 'secondary', size = 'lg', showChecksum = false }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [sha256, setSha256] = useState(null);
  const [safebrowsing, setSafebrowsing] = useState(null);
  const [copied, setCopied] = useState(false);

  async function getRecaptchaToken() {
    const siteKey = process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY;
    if (siteKey && typeof window !== 'undefined' && window.grecaptcha) {
      try {
        const token = await window.grecaptcha.execute(siteKey, { action: 'download' });
        return token;
      } catch (e) {
        console.warn('reCAPTCHA execute failed', e);
        return null;
      }
    }
    return null;
  }

  async function handleClick() {
    setError(null);
    setLoading(true);
    try {
      const token = await getRecaptchaToken();

      const res = await fetch('/api/get-apk-url', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ app, recaptchaToken: token }),
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err?.error || `Server returned ${res.status}`);
      }

      const data = await res.json();
      if (data?.url) {
        // Save metadata for UX (checksum / safebrowsing) only when the caller wants to show it
        if (showChecksum && data.sha256) setSha256(data.sha256);
        if (showChecksum && data.safeBrowsing) setSafebrowsing(data.safeBrowsing);

        // Open the signed URL in a new tab so the browser handles SafeBrowsing / download prompt.
        // On mobile, the browser's download prompt can change the viewport when shown; when the user
        // returns to the site we restore the Get Started card into view to prevent a "zoomed"/broken look.
        const newWin = window.open(data.url, '_blank');

        const onVisibility = () => {
          if (document.visibilityState === 'visible') {
            try {
              const el = document.getElementById('get-started');
              if (el) {
                const headerEl = document.querySelector('header');
                const headerHeight = headerEl ? headerEl.offsetHeight : 80;
                const extraSpacing = 24;
                const top = el.getBoundingClientRect().top + window.pageYOffset - (headerHeight + extraSpacing);
                window.scrollTo({ top, behavior: 'smooth' });

                const heading = el.querySelector('h2');
                if (heading) {
                  heading.setAttribute('tabindex', '-1');
                  heading.focus({ preventScroll: true });
                }
              }
            } catch (e) {
              // ignore
            }
            document.removeEventListener('visibilitychange', onVisibility);
            if (newWin && !newWin.closed) {
              try { newWin.focus(); } catch(e) {}
            }
          }
        };

        document.addEventListener('visibilitychange', onVisibility);
      } else {
        throw new Error('No URL returned from server');
      }
    } catch (e) {
      console.error(e);
      setError(e.message || 'Download failed');
    } finally {
      setLoading(false);
    }
  }

  const btnLabel = label || (app === 'parent' ? 'Download Parent App' : 'Download Child App');

  return (
    <div data-component-id="src/components/DownloadApkButton.jsx:106:4" data-component-path="src/components/DownloadApkButton.jsx" data-component-line="106" data-component-end-line="144" data-component-file="DownloadApkButton.jsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20flex-col%20items-stretch%20gap-2%22%7D" className="flex flex-col items-stretch gap-2">
      <Button data-component-id="src/components/DownloadApkButton.jsx:107:6" data-component-path="src/components/DownloadApkButton.jsx" data-component-line="107" data-component-end-line="109" data-component-file="DownloadApkButton.jsx" data-component-name="Button" data-component-content="%7B%22elementName%22%3A%22Button%22%2C%22className%22%3A%22%5Bvar%3AclassName%5D%22%7D" variant={variant} size={size} className={className} onClick={handleClick} loading={loading}>
        {btnLabel}
      </Button>
      {error && <p data-component-id="src/components/DownloadApkButton.jsx:110:16" data-component-path="src/components/DownloadApkButton.jsx" data-component-line="110" data-component-end-line="110" data-component-file="DownloadApkButton.jsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-sm%20text-red-400%20mt-1%22%7D" className="text-sm text-red-400 mt-1">{error}</p>}
      {showChecksum && sha256 && (
        <p data-component-id="src/components/DownloadApkButton.jsx:112:8" data-component-path="src/components/DownloadApkButton.jsx" data-component-line="112" data-component-end-line="139" data-component-file="DownloadApkButton.jsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-xs%20text-global-4%20mt-1%20flex%20items-center%20gap-2%22%7D" className="text-xs text-global-4 mt-1 flex items-center gap-2">
          <span data-component-id="src/components/DownloadApkButton.jsx:113:10" data-component-path="src/components/DownloadApkButton.jsx" data-component-line="113" data-component-end-line="113" data-component-file="DownloadApkButton.jsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22break-words%20max-w-full%20font-mono%20text-%5B11px%5D%22%7D" className="break-words max-w-full font-mono text-[11px]">{sha256}</span>
          <div data-component-id="src/components/DownloadApkButton.jsx:114:10" data-component-path="src/components/DownloadApkButton.jsx" data-component-line="114" data-component-end-line="138" data-component-file="DownloadApkButton.jsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20items-center%20gap-2%22%7D" className="flex items-center gap-2">
            <a data-component-id="src/components/DownloadApkButton.jsx:115:12" data-component-path="src/components/DownloadApkButton.jsx" data-component-line="115" data-component-end-line="122" data-component-file="DownloadApkButton.jsx" data-component-name="a" data-component-content="%7B%22elementName%22%3A%22a%22%2C%22className%22%3A%22underline%20text-xs%22%2C%22textContent%22%3A%22View%20on%20VirusTotal%22%7D"
              href={`https://www.virustotal.com/gui/search/${sha256}`}
              target="_blank"
              rel="noreferrer"
              className="underline text-xs"
            >
              View on VirusTotal
            </a>
            <button data-component-id="src/components/DownloadApkButton.jsx:123:12" data-component-path="src/components/DownloadApkButton.jsx" data-component-line="123" data-component-end-line="137" data-component-file="DownloadApkButton.jsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%2C%22type%22%3A%22button%22%2C%22className%22%3A%22text-xs%20underline%22%7D"
              type="button"
              className="text-xs underline"
              onClick={async () => {
                try {
                  await navigator.clipboard.writeText(sha256);
                  setCopied(true);
                  setTimeout(() => setCopied(false), 2000);
                } catch (e) {
                  console.warn('copy failed', e);
                }
              }}
            >
              {copied ? 'Copied' : 'Copy'}
            </button>
          </div>
        </p>
      )}
      {showChecksum && safebrowsing && safebrowsing.matches && (
        <p data-component-id="src/components/DownloadApkButton.jsx:142:8" data-component-path="src/components/DownloadApkButton.jsx" data-component-line="142" data-component-end-line="142" data-component-file="DownloadApkButton.jsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-xs%20text-yellow-400%20mt-1%22%2C%22textContent%22%3A%22Warning%3A%20Safe%20Browsing%20reported%20matches%20for%20this%20URL.%22%7D" className="text-xs text-yellow-400 mt-1">Warning: Safe Browsing reported matches for this URL.</p>
      )}
    </div>
  );
}
